import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Divider,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import {
  useAddSftpTenantConfigMutation,
  useUpdateSftpTenantConfigMutation,
  useGetSftpTenantConfigQuery,
  useAddSftpDestinationMutation,
  useUpdateSftpDestinationMutation,
  useRemoveSftpDestinationMutation,
  useToggleSftpDestinationMutation,
  useAddSftpMongoDestinationMutation,
  useUpdateSftpMongoDestinationMutation,
  useRemoveSftpMongoDestinationMutation,
  useToggleSftpMongoDestinationMutation,
  useUpdateSftpMongoColumnMapsMutation,
  useUpdateSftpAggregationConfigMutation,
} from '@/services/graphql';
import type { SqlColumnMapDataInput, MongoColumnMapDataInput } from '@/services/graphql';
import { showMutationErrors, handleMutationSuccess, extractFieldErrors } from '@/utils/errorHandling';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import { SftpDestinationsList, type Destination, type SqlColumn } from './SftpDestinationsList';
import { SftpMongoDestinationsList, type MongoDestination } from './SftpMongoDestinationsList';
import type { AggregationFormData } from './SftpMongoAggregationDialog';
import { sourcePasswordForUpdate, connectionStringForAdd, connectionStringForUpdate } from './helpers/sftpSecretInputs';

interface SftpTenantConfigDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  onDelete?: () => void;
  configId?: string;
  mode: 'create' | 'edit';
  initialValues?: Partial<FormData>;
}

interface FormData {
  configId: string;
  configName: string;
  podId: string;
  enabled: boolean;
  batchSize: number;
  sourceType: string;
  sourceEndpoint: string;
  sourcePort: number;
  sourceDirectory: string;
  sourceUsername: string;
  sourcePassword: string;
  sourceFilePattern: string;
}

const initialFormData: FormData = {
  configId: '',
  configName: '',
  podId: '',
  enabled: true,
  batchSize: 100,
  sourceType: 'SFTP',
  sourceEndpoint: '',
  sourcePort: 22,
  sourceDirectory: '',
  sourceUsername: '',
  sourcePassword: '',
  sourceFilePattern: '*.*',
};

export const SftpTenantConfigDialog: React.FC<SftpTenantConfigDialogProps> = ({
  open,
  onClose,
  onSuccess,
  onDelete,
  configId,
  mode,
  initialValues,
}) => {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [mongoDestinations, setMongoDestinations] = useState<MongoDestination[]>([]);

  // Query existing config if in edit mode
  const { data: existingConfig, isLoading: isLoadingConfig, refetch: refetchConfig } = useGetSftpTenantConfigQuery(
    { configId: configId || '' },
    { skip: mode === 'create' || !configId }
  );

  const [addConfig, { isLoading: isAdding }] = useAddSftpTenantConfigMutation();
  const [updateConfig, { isLoading: isUpdating }] = useUpdateSftpTenantConfigMutation();
  const [addDestination, { isLoading: isAddingDest }] = useAddSftpDestinationMutation();
  const [updateDestination, { isLoading: isUpdatingDest }] = useUpdateSftpDestinationMutation();
  const [removeDestination, { isLoading: isRemovingDest }] = useRemoveSftpDestinationMutation();
  const [toggleDestination, { isLoading: isTogglingDest }] = useToggleSftpDestinationMutation();
  const [addMongoDest, { isLoading: isAddingMongo }] = useAddSftpMongoDestinationMutation();
  const [updateMongoDest, { isLoading: isUpdatingMongo }] = useUpdateSftpMongoDestinationMutation();
  const [removeMongoDest, { isLoading: isRemovingMongo }] = useRemoveSftpMongoDestinationMutation();
  const [toggleMongoDest, { isLoading: isTogglingMongo }] = useToggleSftpMongoDestinationMutation();
  const [updateMongoColumns, { isLoading: isUpdatingMongoCols }] = useUpdateSftpMongoColumnMapsMutation();
  const [updateAggConfig, { isLoading: isUpdatingAgg }] = useUpdateSftpAggregationConfigMutation();

  const isLoading = isAdding || isUpdating || isAddingDest || isUpdatingDest || isRemovingDest || isTogglingDest
    || isAddingMongo || isUpdatingMongo || isRemovingMongo || isTogglingMongo || isUpdatingMongoCols || isUpdatingAgg;

  // Initialize form data
  useEffect(() => {
    if (mode === 'edit' && existingConfig?.sftpTenantConfig) {
      const config = existingConfig.sftpTenantConfig;
      setFormData({
        configId: config.configId,
        configName: config.configName,
        podId: config.podId,
        enabled: config.enabled,
        batchSize: config.batchSize,
        sourceType: config.source.type,
        sourceEndpoint: config.source.endpoint,
        sourcePort: config.source.port,
        sourceDirectory: config.source.directory,
        sourceUsername: config.source.username,
        sourcePassword: '',
        sourceFilePattern: config.source.filePattern,
      });
      setDestinations((config.destinations || []) as Destination[]);
      setMongoDestinations((config.mongoDestinations || []) as MongoDestination[]);
    } else if (mode === 'create') {
      const generatedConfigId = uuidv4().replace(/-/g, '');
      setFormData({
        ...initialFormData,
        ...initialValues,
        configId: generatedConfigId,
      });
      setDestinations([]);
      setMongoDestinations([]);
    }
  }, [mode, existingConfig, initialValues]);

  const handleFieldChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.configName) newErrors.configName = 'Config Name is required';
    if (!formData.podId) newErrors.podId = 'Pod ID is required';
    if (!formData.sourceEndpoint) newErrors.sourceEndpoint = 'Source Endpoint is required';
    if (!formData.sourceDirectory) newErrors.sourceDirectory = 'Source Directory is required';
    if (!formData.sourceUsername) newErrors.sourceUsername = 'Source Username is required';
    if (mode === 'create' && !formData.sourcePassword) newErrors.sourcePassword = 'Source Password is required';
    if (!formData.sourceFilePattern) newErrors.sourceFilePattern = 'Source File Pattern is required';

    if (formData.batchSize <= 0) newErrors.batchSize = 'Batch Size must be greater than 0';
    if (formData.sourcePort <= 0 || formData.sourcePort > 65535) newErrors.sourcePort = 'Source Port must be between 1 and 65535';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      const mutationData = {
        configId: formData.configId,
        configName: formData.configName,
        podId: formData.podId,
        enabled: formData.enabled,
        batchSize: formData.batchSize,
        sourceType: formData.sourceType,
        sourceEndpoint: formData.sourceEndpoint,
        sourcePort: formData.sourcePort,
        sourceDirectory: formData.sourceDirectory,
        sourceUsername: formData.sourceUsername,
        sourceFilePattern: formData.sourceFilePattern,
      };

      const result: any = mode === 'create'
        ? await addConfig({ input: { ...mutationData, sourcePassword: formData.sourcePassword } }).unwrap()
        : await updateConfig({ input: { ...mutationData, sourcePassword: sourcePasswordForUpdate(formData.sourcePassword) } }).unwrap();
      
      const payload = mode === 'create' ? result.addSftpTenantConfig : result.updateSftpTenantConfig;
      const isSuccess = payload && 
                       payload.sftpTenantConfig && 
                       (!payload.errors || payload.errors.length === 0);
      
      if (isSuccess) {
        handleMutationSuccess(
          mode === 'create' 
            ? 'SFTP tenant configuration created successfully' 
            : 'SFTP tenant configuration updated successfully'
        );
        
        if (mode === 'create') {
          // After creating config, inform user about destinations
          toast.info('Configuration created! You can now add destinations.');
        }
        
        if (onSuccess) {
          onSuccess();
        }
        if (mode === 'create') {
          onClose();
        }
      } else if (payload && payload.errors && payload.errors.length > 0) {
        showMutationErrors(payload);
        const fieldErrors = extractFieldErrors(payload);
        if (Object.keys(fieldErrors).length > 0) {
          setErrors(fieldErrors);
        }
      }
    } catch (error: any) {
      if (error.data) {
        const result = error.data;
        if (result.errors && result.errors.length > 0) {
          showMutationErrors(result);
          const fieldErrors = extractFieldErrors(result);
          if (Object.keys(fieldErrors).length > 0) {
            setErrors(fieldErrors);
          }
        }
      } else {
        toast.error('Failed to save configuration. Please check your connection and try again.');
      }
    }
  };

  const handleAddDestination = async (destination: Destination) => {
    try {
      const result = await addDestination({
        input: {
          configId: formData.configId,
          destination: {
            destinationId: destination.destinationId,
            type: destination.type,
            connectionString: connectionStringForAdd(destination.connectionString),
            tableName: destination.tableName,
            schemaName: destination.schemaName,
            sqlBeforeScript: destination.sqlBeforeScript || null,
            sqlAfterScript: destination.sqlAfterScript || null,
            scriptTimeout: destination.scriptTimeout,
            isEnabled: destination.isEnabled,
            sqlColumns: (destination.sqlColumns || []) as SqlColumnMapDataInput[],
          },
        },
      }).unwrap();

      if (result && result.addSftpDestination && result.addSftpDestination.sftpTenantConfig) {
        handleMutationSuccess('Destination added successfully');
        await refetchConfig();
      }
    } catch (error) {
      toast.error('Failed to add destination');
    }
  };

  const handleUpdateDestination = async (destination: Destination) => {
    try {
      const result = await updateDestination({
        input: {
          configId: formData.configId,
          destinationId: destination.destinationId,
          destination: {
            destinationId: destination.destinationId,
            type: destination.type,
            connectionString: connectionStringForUpdate(destination.connectionString),
            tableName: destination.tableName,
            schemaName: destination.schemaName,
            sqlBeforeScript: destination.sqlBeforeScript || null,
            sqlAfterScript: destination.sqlAfterScript || null,
            scriptTimeout: destination.scriptTimeout,
            isEnabled: destination.isEnabled,
            sqlColumns: (destination.sqlColumns || []) as SqlColumnMapDataInput[],
          },
        },
      }).unwrap();

      if (result && result.updateSftpDestination && result.updateSftpDestination.sftpTenantConfig) {
        handleMutationSuccess('Destination updated successfully');
        await refetchConfig();
      }
    } catch (error) {
      toast.error('Failed to update destination');
    }
  };

  const handleRemoveDestination = async (destinationId: string) => {
    try {
      const result = await removeDestination({
        input: {
          configId: formData.configId,
          destinationId,
        },
      }).unwrap();

      if (result && result.removeSftpDestination && result.removeSftpDestination.sftpTenantConfig) {
        handleMutationSuccess('Destination removed successfully');
        await refetchConfig();
      } else if (result?.removeSftpDestination?.errors?.length) {
        showMutationErrors({ errors: result.removeSftpDestination.errors });
      }
    } catch (error) {
      toast.error('Failed to remove destination');
    }
  };

  const handleToggleDestination = async (destinationId: string) => {
    try {
      const result = await toggleDestination({
        input: {
          configId: formData.configId,
          destinationId,
          enable: !destinations.find(d => d.destinationId === destinationId)?.isEnabled,
        },
      }).unwrap();

      if (result && result.toggleSftpDestination && result.toggleSftpDestination.sftpTenantConfig) {
        handleMutationSuccess('Destination toggled successfully');
        await refetchConfig();
      }
    } catch (error) {
      toast.error('Failed to toggle destination');
    }
  };

  const handleUpdateSqlColumns = async (destinationId: string, columns: SqlColumn[]) => {
    const destination = destinations.find(d => d.destinationId === destinationId);
    if (destination) {
      await handleUpdateDestination({
        ...destination,
        sqlColumns: columns,
      });
    }
  };

  const handleAddMongoDestination = async (destination: MongoDestination) => {
    try {
      const result = await addMongoDest({
        input: {
          configId: formData.configId,
          destination: {
            destinationId: destination.destinationId,
            connectionString: connectionStringForAdd(destination.connectionString),
            databaseName: destination.databaseName,
            collectionName: destination.collectionName,
            isEnabled: destination.isEnabled,
            mongoColumns: [],
          },
        },
      }).unwrap();

      if (result?.addSftpMongoDestination?.sftpTenantConfig) {
        handleMutationSuccess('MongoDB destination added successfully');
        await refetchConfig();
      }
    } catch (error) {
      toast.error('Failed to add MongoDB destination');
    }
  };

  const handleUpdateMongoDestination = async (destination: MongoDestination) => {
    try {
      const existing = mongoDestinations.find(d => d.destinationId === destination.destinationId);
      const existingColumns = (existing?.mongoColumns ?? []).map(c => ({
        fieldName: c.fieldName,
        type: c.type,
        csvHeader: c.csvHeader || null,
      })) as MongoColumnMapDataInput[];
      const existingAggregation = existing?.aggregation
        ? {
            groupFields: existing.aggregation.groupFields,
            aggregationFields: existing.aggregation.aggregationFields.map(f => ({
              outputFieldName: f.outputFieldName,
              sourceFieldName: f.sourceFieldName,
              operator: f.operator,
            })),
            targetCollection: existing.aggregation.targetCollection,
            whenMatched: existing.aggregation.whenMatched,
            whenNotMatched: existing.aggregation.whenNotMatched,
            mergeOnFields: existing.aggregation.mergeOnFields,
            useAccumulativeMerge: existing.aggregation.useAccumulativeMerge,
          }
        : null;
      const result = await updateMongoDest({
        input: {
          configId: formData.configId,
          destinationId: destination.destinationId,
          destination: {
            destinationId: destination.destinationId,
            connectionString: connectionStringForUpdate(destination.connectionString),
            databaseName: destination.databaseName,
            collectionName: destination.collectionName,
            isEnabled: destination.isEnabled,
            mongoColumns: existingColumns,
            aggregation: existingAggregation,
          },
        },
      }).unwrap();

      if (result?.updateSftpMongoDestination?.sftpTenantConfig) {
        handleMutationSuccess('MongoDB destination updated successfully');
        await refetchConfig();
      }
    } catch (error) {
      toast.error('Failed to update MongoDB destination');
    }
  };

  const handleRemoveMongoDestination = async (destinationId: string) => {
    try {
      const result = await removeMongoDest({
        input: { configId: formData.configId, destinationId },
      }).unwrap();

      if (result?.removeSftpMongoDestination?.sftpTenantConfig) {
        handleMutationSuccess('MongoDB destination removed successfully');
        await refetchConfig();
      } else if (result?.removeSftpMongoDestination?.errors?.length) {
        showMutationErrors({ errors: result.removeSftpMongoDestination.errors });
      }
    } catch (error) {
      toast.error('Failed to remove MongoDB destination');
    }
  };

  const handleToggleMongoDestination = async (destinationId: string, enable: boolean) => {
    try {
      const result = await toggleMongoDest({
        input: { configId: formData.configId, destinationId, enable },
      }).unwrap();

      if (result?.toggleSftpMongoDestination?.sftpTenantConfig) {
        handleMutationSuccess('MongoDB destination toggled successfully');
        await refetchConfig();
      }
    } catch (error) {
      toast.error('Failed to toggle MongoDB destination');
    }
  };

  const handleUpdateMongoColumns = async (destinationId: string, columns: any[]) => {
    try {
      const result = await updateMongoColumns({
        input: {
          configId: formData.configId,
          destinationId,
          mongoColumns: columns.map(c => ({
            fieldName: c.fieldName,
            type: c.type,
            csvHeader: c.csvHeader || null,
          })),
        },
      }).unwrap();

      if (result?.updateSftpMongoColumnMaps?.sftpTenantConfig) {
        handleMutationSuccess('MongoDB column mappings updated successfully');
        await refetchConfig();
      }
    } catch (error) {
      toast.error('Failed to update MongoDB column mappings');
    }
  };

  const handleUpdateAggregation = async (destinationId: string, data: AggregationFormData) => {
    try {
      const result = await updateAggConfig({
        input: {
          configId: formData.configId,
          destinationId,
          aggregation: {
            targetCollection: data.targetCollection,
            groupFields: data.groupFields,
            mergeOnFields: data.mergeOnFields,
            aggregationFields: data.aggregationFields.map(f => ({
              sourceFieldName: f.sourceFieldName,
              outputFieldName: f.outputFieldName,
              operator: f.operator,
            })),
            whenMatched: data.whenMatched,
            whenNotMatched: data.whenNotMatched,
            useAccumulativeMerge: data.useAccumulativeMerge,
          },
        },
      }).unwrap();

      if (result?.updateSftpAggregationConfig?.sftpTenantConfig) {
        handleMutationSuccess('Aggregation config updated successfully');
        await refetchConfig();
      }
    } catch (error) {
      toast.error('Failed to update aggregation config');
    }
  };

  const handleClose = () => {
    setFormData(initialFormData);
    setErrors({});
    setDestinations([]);
    setMongoDestinations([]);
    onClose();
  };

  if (mode === 'edit' && isLoadingConfig) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {mode === 'create' ? 'Create SFTP Tenant Configuration' : 'Edit SFTP Tenant Configuration'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold">Basic Information</Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Config Name"
              value={formData.configName}
              onChange={(e) => handleFieldChange('configName', e.target.value)}
              error={!!errors.configName}
              helperText={errors.configName}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Pod ID"
              value={formData.podId}
              onChange={(e) => handleFieldChange('podId', e.target.value)}
              error={!!errors.podId}
              helperText={errors.podId}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.enabled}
                  onChange={(e) => handleFieldChange('enabled', e.target.checked)}
                />
              }
              label="Enabled"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Batch Size"
              value={formData.batchSize}
              onChange={(e) => handleFieldChange('batchSize', parseInt(e.target.value, 10) || 0)}
              error={!!errors.batchSize}
              helperText={errors.batchSize}
            />
          </Grid>

          {/* Source Configuration */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>Source Configuration</Typography>
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Source Type</InputLabel>
              <Select
                value={formData.sourceType}
                label="Source Type"
                onChange={(e) => handleFieldChange('sourceType', e.target.value)}
              >
                <MenuItem value="SFTP">SFTP</MenuItem>
                <MenuItem value="FTP">FTP</MenuItem>
                <MenuItem value="FTPS">FTPS</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Source Endpoint"
              value={formData.sourceEndpoint}
              onChange={(e) => handleFieldChange('sourceEndpoint', e.target.value)}
              error={!!errors.sourceEndpoint}
              helperText={errors.sourceEndpoint}
            />
          </Grid>

          <Grid item xs={12} sm={2}>
            <TextField
              fullWidth
              type="number"
              label="Port"
              value={formData.sourcePort}
              onChange={(e) => handleFieldChange('sourcePort', parseInt(e.target.value, 10) || 0)}
              error={!!errors.sourcePort}
              helperText={errors.sourcePort}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Source Directory"
              value={formData.sourceDirectory}
              onChange={(e) => handleFieldChange('sourceDirectory', e.target.value)}
              error={!!errors.sourceDirectory}
              helperText={errors.sourceDirectory}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="File Pattern"
              value={formData.sourceFilePattern}
              onChange={(e) => handleFieldChange('sourceFilePattern', e.target.value)}
              error={!!errors.sourceFilePattern}
              helperText={errors.sourceFilePattern}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Username"
              value={formData.sourceUsername}
              onChange={(e) => handleFieldChange('sourceUsername', e.target.value)}
              error={!!errors.sourceUsername}
              helperText={errors.sourceUsername}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="password"
              label="Password"
              value={formData.sourcePassword}
              onChange={(e) => handleFieldChange('sourcePassword', e.target.value)}
              error={!!errors.sourcePassword}
              helperText={errors.sourcePassword || (mode === 'edit' ? 'Leave blank to keep the current password' : undefined)}
              autoComplete="new-password"
            />
          </Grid>

          {/* Destinations - Only show in edit mode */}
          {mode === 'edit' && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
              </Grid>
              <Grid item xs={12}>
                <SftpDestinationsList
                  destinations={destinations}
                  configId={formData.configId}
                  onAddDestination={handleAddDestination}
                  onUpdateDestination={handleUpdateDestination}
                  onRemoveDestination={handleRemoveDestination}
                  onToggleDestination={handleToggleDestination}
                  onUpdateSqlColumns={handleUpdateSqlColumns}
                  isLoading={isLoading}
                />
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
              </Grid>
              <Grid item xs={12}>
                <SftpMongoDestinationsList
                  destinations={mongoDestinations}
                  onAddDestination={handleAddMongoDestination}
                  onUpdateDestination={handleUpdateMongoDestination}
                  onRemoveDestination={handleRemoveMongoDestination}
                  onToggleDestination={handleToggleMongoDestination}
                  onUpdateMongoColumns={handleUpdateMongoColumns}
                  onUpdateAggregation={handleUpdateAggregation}
                  isLoading={isLoading}
                />
              </Grid>
            </>
          )}

          {mode === 'create' && (
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mt: 2 }}>
                After creating this configuration, you'll be able to add multiple destinations with their own SQL column mappings.
              </Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ justifyContent: mode === 'edit' && onDelete ? 'space-between' : 'flex-end' }}>
        {mode === 'edit' && onDelete && (
          <Button
            onClick={onDelete}
            color="error"
            startIcon={<DeleteIcon />}
            disabled={isLoading}
          >
            Delete
          </Button>
        )}
        <Box>
          <Button
            onClick={handleClose}
            startIcon={<CancelIcon />}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={isLoading}
            sx={{ ml: 1 }}
          >
            {isLoading ? 'Saving...' : (mode === 'create' ? 'Create' : 'Save')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};